import {
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { NotificationHandler } from '../../types';
import { isAvailable } from '../utils';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import { PostMessageOption } from 'types/index';
import { mobileUI } from './ui';
import { Notification } from '../../utils/type';
import type { SafeArea, KeyboardRect } from './types';
import { getShareCapabilities, shareContent, saveImageToAlbum } from './share';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.getSafeArea
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0+
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getSafeArea(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<SafeArea>> {
  return isAvailable('mobile.getSafeArea').then(() => {
    return postJsMessage(API.getSafeArea, {}, options);
  });
}

const SafeAreaNotification = ['UnifyNotify_OnSafeAreaChange'];

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.onSafeAreaChange
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0+
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function onSafeAreaChange(
  onSafeAreaChangeCallback: NotificationHandler<
    NativeAPIResponseMessage<SafeArea>
  >
) {
  await isAvailable('mobile.onSafeAreaChange');
  registerSpecificNotificationHandler(
    SafeAreaNotification,
    onSafeAreaChangeCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.offSafeAreaChange
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.5.0+
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function offSafeAreaChange(
  onSafeAreaChangeCallback: NotificationHandler<
    NativeAPIResponseMessage<SafeArea>
  >
) {
  await isAvailable('mobile.offSafeAreaChange');
  unregisterSpecificNotificationHandler(
    SafeAreaNotification,
    onSafeAreaChangeCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.getKeyboardRect
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0+
 * android    6.6.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getKeyboardRect(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<KeyboardRect>> {
  await isAvailable('mobile.getKeyboardRect');
  return postJsMessage(API.getKeyboardRect, {}, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.showKeyboard
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        NotSupport
 * android    6.5.10+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function showKeyboard(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<{}>> {
  await isAvailable('mobile.showKeyboard');
  return postJsMessage(API.showKeyboard, {}, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.onKeyboardRectChange
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0+
 * android    6.6.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onKeyboardRectChange(
  keyboardRectChangeHandler: (
    KeyboardRectChangeNotification: Notification<KeyboardRect>
  ) => void
) {
  registerSpecificNotificationHandler(
    'UnifyNotify_OnKeyboardRectChange',
    keyboardRectChangeHandler
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * mobile.offKeyboardRectChange
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.6.0+
 * android    6.6.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offKeyboardRectChange(
  keyboardRectChangeHandler: (
    KeyboardRectChangeNotification: Notification<KeyboardRect>
  ) => void
) {
  unregisterSpecificNotificationHandler(
    'UnifyNotify_OnKeyboardRectChange',
    keyboardRectChangeHandler
  );
}

// Export share functions
export { getShareCapabilities, shareContent, saveImageToAlbum };

export default {
  getSafeArea,
  onSafeAreaChange,
  offSafeAreaChange,
  getKeyboardRect,
  showKeyboard,
  onKeyboardRectChange,
  offKeyboardRectChange,
  getShareCapabilities,
  shareContent,
  saveImageToAlbum,
  ...mobileUI,
};
